import React, { useState } from 'react';

import { CircularProgress } from '@mui/material';
import ConfirmationModal from '@apigrowfinance/pbolt-confirmation-modal';
import { PDialog } from '@apigrowfinance/pbolt-dialog';
import { AxiosError } from 'axios';
// eslint-disable-next-line import/no-unresolved
import { Notify } from 'notify/services/Notify';
import { FormattedMessage, useIntl } from 'react-intl';

import { IPatchUpdateAccountStatusDetails } from '@clients/accountsClient';
import { useAccount } from '@contexts/AccountProvider';
import useGetAccountStatus from '@hooks/queries/useGetAccountStatuses';
import usePatchUpdateAccountStatus from '@hooks/queries/usePatchUpdateAccountStatus';
import accountStatusTranslations from '@messages/accountStatus';
import AccountStatusForm from '@pages/accountContainer/AccountStatus/AccountStatusForm';
import getEnumTranslation from '@utils/getEnumTranslation';

interface AccountChangeStatusProps {
    activeStatus: string;
    closeChangeStatus: () => void;
}

const notify = Notify.getInstance();

const AccountChangeStatus = ({
    activeStatus,
    closeChangeStatus,
}: AccountChangeStatusProps) => {
    const { formatMessage } = useIntl();
    const [stage, setStage] = useState<'form' | 'confirmation'>('form');
    const [statusChangeDetails, setStatusChangeDetails] = useState(null);
    const { account } = useAccount();
    const {
        data: statuses,
        isError: isStatusesError,
        isLoading: isLoadingStatuses,
    } = useGetAccountStatus();

    const { error, isLoading, mutate } = usePatchUpdateAccountStatus(
        account?.account_id,
        () => {
            notify.addNotification(
                formatMessage({
                    id: 'usePatchUpdateAccountStatus.success',
                    defaultMessage: 'Account status updated',
                }),
            );
            closeChangeStatus();
        },
        () => {
            setStage('form');
        },
    );

    const handleCloseForm = () => {
        closeChangeStatus();
    };

    const submitStatusChange = () => {
        mutate(statusChangeDetails);
    };
    const readyToSubmitData = (data: IPatchUpdateAccountStatusDetails) => {
        setStatusChangeDetails(data);
        setStage('confirmation');
    };
    const cancelChange = () => {
        setStage('form');
    };
    const newStatus =
        statusChangeDetails?.status != null
            ? getEnumTranslation(statusChangeDetails?.status, {
                  messages: accountStatusTranslations,
                  formatMessage,
              })
            : '';

    // We can't show the form if we don't have the statuses
    // An error notification will be visible instead
    if (isStatusesError) {
        return null;
    }

    return (
        <>
            <PDialog
                dialogTitle={
                    <FormattedMessage
                        id="AccountStatus.changeTitle"
                        defaultMessage="Change account status"
                    />
                }
                dialogSubtitle={
                    <FormattedMessage
                        id="AccountStatus.changeDesc"
                        defaultMessage="Changing account status will display to all
                            operators and send a notification."
                    />
                }
                onClose={handleCloseForm}
                open={stage === 'form'}>
                <AccountStatusForm
                    isLoadingStatuses={isLoadingStatuses}
                    accountStatuses={statuses}
                    activeStatus={activeStatus}
                    closeModal={handleCloseForm}
                    readyToSubmitData={readyToSubmitData}
                    error={error as AxiosError}
                />
            </PDialog>

            <ConfirmationModal
                title={
                    <FormattedMessage
                        id="AccountChangeStatus.ConfirmationTitle"
                        defaultMessage="Are you sure?"
                    />
                }
                description={
                    activeStatus && (
                        <FormattedMessage
                            id="AccountChangeStatus.ConfirmationDesc"
                            defaultMessage="Are you sure you want to change ''{accountName}'' account status from ''{activeStatus}'' to ''{newStatus}''."
                            values={{
                                activeStatus: getEnumTranslation(activeStatus, {
                                    messages: accountStatusTranslations,
                                }),
                                accountName: account?.name,
                                newStatus: newStatus,
                            }}
                        />
                    )
                }
                open={stage === 'confirmation'}
                positiveCallback={submitStatusChange}
                disableButtons={isLoading}
                negativeCallback={cancelChange}
                positiveButtonText={
                    isLoading ? (
                        <CircularProgress
                            size="1rem"
                            sx={{
                                color: 'primary.contrastText',
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="AccountChangeStatus.proceed"
                            defaultMessage="Yes, proceed"
                        />
                    )
                }
                negativeButtonText={
                    <FormattedMessage
                        id="AccountChangeStatus.NoCancel"
                        defaultMessage="No, cancel"
                    />
                }
            />
        </>
    );
};

export default AccountChangeStatus;
